import { reportNuveiSdkError } from "@/observability/nuvei";
import { loadScript } from "@/utils/helpers";
import type {
  NuveiCreateApplePayPaymentOptions,
  NuveiCreateApplePayPaymentResult,
} from "./applepay";

const APPLE_PAY_JS_SDK_URL =
  "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";

export const loadApplePaySDK = async (): Promise<void> => {
  await loadScript(APPLE_PAY_JS_SDK_URL, { crossorigin: true });
};

/**
 * Initializes Apple Pay: loads Apple's JS SDK so the <apple-pay-button> and ApplePaySession are available.
 * Payment flow is handled by the SafeCharge SDK (createApplePayPayment); no Nuvei sc_api_applepay needed.
 */
export const initApplePay = async (): Promise<void> => {
  await loadApplePaySDK();
};

/**
 * Checks if Apple Pay is supported on this device.
 * Uses window.ApplePaySession.canMakePayments().
 */
export const isApplePaySupported = (): boolean => {
  if (typeof window === "undefined" || !window.ApplePaySession) {
    return false;
  }
  return window.ApplePaySession.canMakePayments();
};

/**
 * Makes an Apple Pay payment through Nuvei's integration (SafeCharge SDK).
 * Session handling and merchant validation are handled automatically by the Nuvei Apple Pay SDK wrapper.
 * Requires SafeCharge SDK to be initialized (window.safeChargeSdkInstance) and initApplePay() to have run.
 * Options must include total.amount (e.g. "19.99") and total.label; sessionToken, merchantId, merchantSiteId,
 * env, countryCode, currencyCode are required.
 * @throws Error if SafeCharge SDK or createApplePayPayment is not available, or on payment failure.
 */
export const createApplePayPayment = (
  options: NuveiCreateApplePayPaymentOptions
): Promise<NuveiCreateApplePayPaymentResult> => {
  return new Promise<NuveiCreateApplePayPaymentResult>((resolve, reject) => {
    if (!window.safeChargeSdkInstance) {
      reject(new Error("SafeCharge SDK is not initialized"));
      return;
    }
    window.safeChargeSdkInstance.createApplePayPayment(options, (result) => {
      if (result?.errors != null && result.errors !== 0) {
        reportNuveiSdkError("createApplePayPayment", result);
        reject(
          new Error(
            result.errorDescription ??
              "Apple Pay payment could not be completed."
          )
        );
        return;
      }
      const isSuccessErrCode =
        typeof result.errCode === "undefined" ||
        `${result.errCode}` === "0" ||
        result.errCode === 0 ||
        result.errCode === null;
      if (!isSuccessErrCode) {
        reportNuveiSdkError("createApplePayPayment", result);
        reject(new Error("Apple Pay payment failed"));
        return;
      }
      resolve(result);
    });
  });
};
